import React, { FC, useEffect } from "react";
import { AGDialog } from "../../components/AGDialog";
import {
	Box,
	Button,
	FormControl,
	FormHelperText,
	Grid2,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import NumberInput from "../../components/NumberInput";

export interface IAISearchSettingsInputs {
	top_k: number;
	accuracy: number;
	n_probe: number;
}

interface IAISearchSettingsDialogProps {
	open: boolean;
	filters: IAISearchSettingsInputs;
	onClose: () => void;
	onSubmit: (values: IAISearchSettingsInputs) => void;
}

const AI_TOP_K_LIST: number[] = [15, 50, 100, 150, 200, 250];

const AISearchSettingsDialog: FC<IAISearchSettingsDialogProps> = (props) => {
	const { open, filters, onClose, onSubmit } = props;

	const {
		reset,
		control,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<IAISearchSettingsInputs>();

	function handleSubmitForm(formData: IAISearchSettingsInputs) {
		onSubmit(formData);
	}

	useEffect(() => {
		if (open && filters) {
			reset({
				top_k: filters.top_k,
				accuracy: filters.accuracy * 100,
				n_probe: filters.n_probe
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, open]);

	return (
		<AGDialog open={open} onClose={onClose} title="AI Search Advanced Settings" color="primary" maxWidth="sm">
			<Box component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Controller
							control={control}
							name="top_k"
							render={({ field: { value, onChange } }) => (
								<FormControl fullWidth size="small">
									<InputLabel id="top-k-input-label" error={!!errors.top_k}>
										TopK
									</InputLabel>

									<Select
										fullWidth
										label="TopK"
										size="small"
										labelId="top-k-input-label"
										value={value}
										onChange={onChange}
										error={!!errors.top_k}
									>
										{AI_TOP_K_LIST.map((item) => (
											<MenuItem key={item} value={item}>
												{item}
											</MenuItem>
										))}
									</Select>

									{errors.top_k && errors.top_k.message ? (
										<FormHelperText error>{errors.top_k.message.toString()}</FormHelperText>
									) : null}
								</FormControl>
							)}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<NumberInput
							fullWidth
							disableSigned
							disableDecimal
							size="small"
							label="Accuracy"
							minValue={0}
							maxValue={100}
							error={!!errors.accuracy}
							helperText={errors.accuracy && errors.accuracy.message ? errors.accuracy.message.toString() : ""}
							slotProps={{
								input: {
									endAdornment: <InputAdornment position="end">%</InputAdornment>
								}
							}}
							{...register("accuracy")}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<NumberInput
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							size="small"
							label="NProbe"
							minValue={1}
							maxValue={200}
							error={!!errors.n_probe}
							helperText={errors.n_probe && errors.n_probe.message ? errors.n_probe.message.toString() : ""}
							{...register("n_probe")}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
							<Button disableElevation variant="contained" color="primary" type="submit">
								Apply
							</Button>
						</Box>
					</Grid2>
				</Grid2>
			</Box>
		</AGDialog>
	);
};

export default AISearchSettingsDialog;
